import { scry, scryImageUrl } from "@/lib/scryClient";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";

type ScryfallCard = {
  id: string;
  name: string;
  oracle_id?: string;
  set?: string;
  set_name?: string;
  collector_number?: string;
  image_uris?: { small?: string; normal?: string; large?: string };
  card_faces?: Array<{ image_uris?: { small?: string; normal?: string; large?: string } }>;
  prices?: { usd?: string | null; usd_foil?: string | null; usd_etched?: string | null };
  foil?: boolean;
  nonfoil?: boolean;
  finishes?: string[];
};

export type VariantPick = {
  id: string;
  name: string;
  set?: string | null;
  set_name?: string | null;
  collector_number?: string | null;
  image?: string | null;
  foilAvailable: boolean;
  nonfoilAvailable: boolean;
  prices?: { usd?: number | null; usd_foil?: number | null; usd_etched?: number | null } | null;
  // Chosen finish from the picker
  selectedFinish?: "foil" | "nonfoil";
};

export default function TradesVariantPickerModal(props: {
  open: boolean;
  onClose: () => void;
  name: string;
  scryfall_id?: string;
  onSelect: (pick: any) => void;
}) {
  const { open, onClose, name, scryfall_id, onSelect } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variants, setVariants] = useState<ScryfallCard[]>([]);
  const acRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setVariants([]);
    acRef.current?.abort();
    const ac = new AbortController();
    acRef.current = ac;

    async function load() {
      setLoading(true);
      try {
        let oracleId: string | null = null;
        if (scryfall_id) {
          const base = await (await scry.getById(scryfall_id)).json();
          oracleId = base.oracle_id || null;
        }
        let all: ScryfallCard[] = [];
        if (oracleId) {
          // Fetch all prints via oracle_id (paginate)
          let url = `https://api.scryfall.com/cards/search?order=released&unique=prints&include_extras=true&include_variations=true&q=oracleid:${encodeURIComponent(
            oracleId,
          )}`;
          while (url) {
            const res = await fetch(url, { signal: ac.signal });
            if (!res.ok) throw new Error("Failed to load prints");
            const json = (await res.json()) as any;
            all = all.concat(json.data as ScryfallCard[]);
            url = json.has_more ? json.next_page : "";
          }
        } else {
          // Fallback: exact-name search
          const q = `!"${name}"`;
          const data = await scry.search(q, { unique: "prints", order: "released" });
          all = data.data;
        }
        setVariants(all);
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Could not load card variants.");
      } finally {
        setLoading(false);
      }
    }

    void load();
    return () => ac.abort();
  }, [open, scryfall_id, name]);

  const items = useMemo<VariantPick[]>(() => {
    return variants.map((v) => {
      const img =
        v.image_uris?.normal ??
        v.card_faces?.[0]?.image_uris?.normal ??
        (v.id ? scryImageUrl(v.id, "normal") : null);
      const foilAvail = Boolean(v.finishes?.includes("foil") || v.foil);
      const nonfoilAvail = Boolean(v.finishes?.includes("nonfoil") || v.nonfoil || !foilAvail);
      const prices = {
        usd: v.prices?.usd != null ? Number(v.prices.usd) : null,
        usd_foil: v.prices?.usd_foil != null ? Number(v.prices.usd_foil) : null,
        usd_etched: v.prices?.usd_etched != null ? Number(v.prices.usd_etched) : null,
      };
      return {
        id: v.id,
        name: v.name,
        set: v.set ?? null,
        set_name: v.set_name ?? null,
        collector_number: v.collector_number ?? null,
        image: img,
        foilAvailable: foilAvail,
        nonfoilAvailable: nonfoilAvail,
        prices,
      };
    });
  }, [variants]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[95]">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} aria-hidden />
      <div
        className="
          absolute inset-0 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2
          w-full md:w-[min(96vw,900px)]
          h-[100dvh] md:h-[85vh]
          bg-gray-950 border border-slate-700 rounded-none md:rounded-2xl shadow-2xl
          flex flex-col
          pt-[env(safe-area-inset-top)]
        "
        role="dialog"
        aria-modal="true"
        aria-labelledby="variant-picker-title"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-3 sm:px-4 py-3 border-b border-slate-800 bg-gray-950/95 backdrop-blur supports-[backdrop-filter]:bg-gray-950/60">
          <div id="variant-picker-title" className="text-sm md:text-base font-semibold text-white truncate">
            Select a version — {name}
          </div>
          <button
            className="rounded-md border border-white/10 px-3 py-1.5 text-white hover:bg-white/5"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-3 sm:px-4 py-3">
            {loading ? (
              <div className="text-slate-300 text-sm">Loading variants…</div>
            ) : error ? (
              <div className="text-red-300 text-sm">{error}</div>
            ) : items.length === 0 ? (
              <div className="text-slate-300 text-sm">No variants found.</div>
            ) : (
              <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((v) => (
                  <div key={v.id} className="rounded-xl overflow-hidden border border-slate-700/60 bg-black/30 flex flex-col">
                    <div className="aspect-[3/4] bg-black">
                      {v.image ? (
                        <img src={v.image} alt={v.name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      ) : (
                        <div className="text-xs text-slate-400 h-full grid place-items-center">No image</div>
                      )}
                    </div>
                    <div className="p-3 space-y-1 grow">
                      <div className="text-white text-sm font-medium truncate">{v.name}</div>
                      <div className="text-[12px] text-slate-400 truncate">
                        {(v.set_name || v.set || "Set").toString().toUpperCase()} · #{v.collector_number || "—"}
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                        <div className="rounded bg-white/5 p-1.5">
                          <div className="text-[10px] text-white/60">TCG Mid</div>
                          <div className="text-[13px] text-yellow-300 font-semibold">
                            {v.prices?.usd != null ? `$${Number(v.prices.usd).toFixed(2)}` : "—"}
                          </div>
                        </div>
                        <div className="rounded bg-white/5 p-1.5">
                          <div className="text-[10px] text-white/60">Foil</div>
                          <div className="text-[13px] text-purple-300 font-semibold">
                            {v.prices?.usd_foil != null ? `$${Number(v.prices.usd_foil).toFixed(2)}` : "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="px-3 pb-3 pt-0 sticky bottom-0 bg-gradient-to-t from-gray-950/90 to-transparent flex items-center gap-2">
                      <button
                        className="flex-1 rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-200 hover:bg-white/5 disabled:opacity-50"
                        disabled={!v.nonfoilAvailable}
                        onClick={() => onSelect({ ...v, selectedFinish: "nonfoil" })}
                        title="Select regular"
                      >
                        Select
                      </button>
                      <button
                        className="flex-1 rounded border border-purple-700 bg-purple-600/20 text-purple-200 px-3 py-2 hover:bg-purple-600/30 disabled:opacity-50"
                        disabled={!v.foilAvailable}
                        onClick={() => onSelect({ ...v, selectedFinish: "foil" })}
                        title="Select (foil)"
                      >
                        Select Foil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Safe-area bottom padding */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}

